import React, { useState, useEffect, useCallback } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useStarterRecipes } from '@/hooks/useStarterRecipes';
import StarterRecipeCard from './StarterRecipeCard';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';
import { cn } from '@/lib/utils';

interface StarterRecipesSectionProps {
  onRecipeAdded?: () => void;
}

const StarterRecipesSection: React.FC<StarterRecipesSectionProps> = ({
  onRecipeAdded
}) => {
  const { 
    starterRecipes, 
    isLoading, 
    addToCollection, 
    addedRecipeIds,
    isAdding,
    addingRecipeId
  } = useStarterRecipes();

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    setCanScroll(api.canScrollPrev() || api.canScrollNext());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    
    setCount(api.scrollSnapList().length);
    onSelect();
    
    api.on("select", onSelect);
    api.on("reInit", onSelect);

    return () => {
      api?.off("select", onSelect);
      api?.off("reInit", onSelect);
    };
  }, [api, onSelect]);

  const handleAdd = async (recipe: any) => {
    await addToCollection(recipe);
    onRecipeAdded?.();
  };

  if (isLoading) {
    return (
      <div className="mt-6 pt-4 border-t border-border/50">
        <p className="text-sm text-muted-foreground mb-3">Or start with these basics:</p>
        <div className="flex gap-2 md:gap-4 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex-shrink-0 w-[calc(50%-4px)] sm:w-[calc(33.333%-10px)]">
              <div className="space-y-2">
                <Skeleton className="aspect-[4/3] w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Filter out already-added recipes from carousel display
  const displayRecipes = starterRecipes.filter(recipe => !addedRecipeIds.has(recipe.id));

  if (displayRecipes.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 pt-4 border-t border-border/50">
      <p className="text-sm text-muted-foreground mb-3">Or start with these basics:</p>
      <Carousel
        opts={{
          align: "start",
          loop: false,
          containScroll: "trimSnaps",
        }}
        setApi={setApi}
        className="w-full"
      >
        <CarouselContent className="-ml-2 md:-ml-4">
          {displayRecipes.map((recipe) => (
            <CarouselItem 
              key={recipe.id} 
              className="pl-2 md:pl-4 basis-1/2 sm:basis-1/3"
            >
              <StarterRecipeCard
                recipe={recipe}
                onAdd={handleAdd}
                isAdded={addedRecipeIds.has(recipe.id)}
                isAdding={isAdding && addingRecipeId === recipe.id}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        {/* Only show arrows if there's content to scroll */}
        {canScroll && (
          <>
            <CarouselPrevious className="hidden sm:flex -left-4" />
            <CarouselNext className="hidden sm:flex -right-4" />
          </>
        )}
      </Carousel>
      {/* Mobile dots indicator - only show if there are multiple slides */}
      {count > 1 && (
        <div className="flex justify-center gap-1.5 mt-3 sm:hidden">
          {Array.from({ length: count }).map((_, index) => (
            <button
              key={index}
              className={cn(
                "w-2 h-2 rounded-full transition-colors",
                index === current ? "bg-sage" : "bg-muted-foreground/30"
              )}
              onClick={() => api?.scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StarterRecipesSection;
